import React, { useEffect, useState } from 'react';
import { Button } from '../button';
import { deepEquals } from '../common/Helper';
import { usePrevious } from '../common/Hooks';
import './Filters.css';
import {
  FilterOption,
  GenericFilterProps,
  MultiSelectFilterType
} from './types';

type StringFilterProps = GenericFilterProps<string>;
type MultiSelectFilterProps = FilterOptions &
  Partial<GenericFilterProps<MultiSelectFilterType>>;

interface FilterOptions {
  maxHeight?: string | number;
  options: FilterOption[];
}
interface MultiSelectFilterOptionProps {
  option: FilterOption;
  onChange: (option: FilterOption) => void;
}

export const StringFilter = React.forwardRef<HTMLDivElement, StringFilterProps>(
  (props, ref) => {
    const { apply, clear, close, filterValue } = props;
    const [value, setValue] = useState(filterValue || '');

    const prevValue = usePrevious(value);

    useEffect(() => {
      if (prevValue !== undefined && prevValue !== value) {
        apply(value);
      }
    }, [prevValue, value, apply, clear]);

    const onFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape' || e.key === 'Enter') {
        close();
      }
    };

    return (
      <div ref={ref} className="datagrid-filter-container">
        <input
          className="clr-input"
          autoFocus={true}
          value={value}
          onChange={onFilterChange}
          onKeyDown={onKeyDown}
          title="Search text"
        />
      </div>
    );
  }
);

const MultiSelectFilterOption = React.forwardRef<
  HTMLDivElement,
  MultiSelectFilterOptionProps
>((props, ref) => {
  const { option, onChange } = props;
  const { checked, displayValue, id, selectAllType } = option;

  const onOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...option, checked: !!e.target.checked });
  };

  return (
    <div ref={ref} className="clr-checkbox-wrapper">
      <input
        id={id}
        className="clr-checkbox"
        type="checkbox"
        checked={checked}
        name={id}
        value={id}
        onChange={onOptionChange}
      />
      <label className="clr-control-label" htmlFor={id}>
        <span className="datagrid-filter-option">
          {selectAllType ? <strong>{displayValue}</strong> : displayValue}
        </span>
      </label>
    </div>
  );
});

export const MultiSelectFilter = React.forwardRef<
  HTMLDivElement,
  MultiSelectFilterProps
>((props, ref) => {
  const {
    apply,
    clear,
    filterValue,
    maxHeight,
    close
  } = props as FilterOptions & GenericFilterProps<MultiSelectFilterType>;
  const initialOptionValues = filterValue ? filterValue : props.options;
  const [options, setOptions] = useState(initialOptionValues);
  const [search, setSearch] = useState<string>();
  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearch(e.target.value);
  const visibleOptions = search
    ? options
        .filter(
          o =>
            o.displayValue.toLowerCase().includes(search.toLowerCase()) &&
            !o.selectAllType
        )
        .sort(
          (a, b) =>
            a.displayValue.toLowerCase().indexOf(search.toLowerCase()) -
            b.displayValue.toLowerCase().indexOf(search.toLowerCase())
        )
    : options;
  const applyFilter = () => {
    apply(options);
    close();
  };
  const clearFilter = () => {
    setOptions(
      initialOptionValues.map(option => ({
        ...option,
        checked: false
      }))
    );
    clear();
    close();
  };

  const applyBtnDisabled = deepEquals(
    initialOptionValues
      .filter(o => o.checked)
      .sort((o1, o2) => o1.id.localeCompare(o2.id)),
    options.filter(o => o.checked).sort((o1, o2) => o1.id.localeCompare(o2.id))
  );

  const onOptionChange = (option: FilterOption) => {
    if (option.selectAllType) {
      setOptions(
        initialOptionValues.map(opt => ({
          ...opt,
          checked: option.checked
        }))
      );
    } else {
      // if option is unchecked, modify selectAll checkbox to unchecked,
      // if option is checked, check if all the options are checked and mark selectAll option accordingly

      const index = options.findIndex(o => o.id === option.id);
      const selectAllIndex = options.findIndex(o => !!o.selectAllType);

      const newOptions = options.map((o, i) => (i === index ? option : o));
      if (selectAllIndex === -1) {
        // selectAll option is not even present.
        setOptions(newOptions);
      } else {
        const allOptionsChecked =
          newOptions
            .filter(o => !o.selectAllType)
            .findIndex(o => !o.checked) === -1;
        setOptions(
          newOptions.map((o, i) =>
            i === selectAllIndex ? { ...o, checked: allOptionsChecked } : o
          )
        );
      }
    }
  };

  return (
    <div ref={ref} className="datagrid-filter-container">
      <div className="datagrid-filter-search">
        <input
          className="clr-input"
          placeholder="Search.."
          autoFocus={true}
          value={search}
          onChange={onSearchChange}
          aria-label="Filter text"
        />
      </div>
      <div className="datagrid-filter-options" style={{ maxHeight }}>
        {visibleOptions.map((option, index) => (
          <MultiSelectFilterOption
            key={index}
            option={option}
            onChange={onOptionChange}
          />
        ))}
      </div>
      <Button
        class="primary"
        block={true}
        size="sm"
        onClick={applyFilter}
        disabled={applyBtnDisabled}>
        Apply
      </Button>
      {initialOptionValues.some(o => o.checked) && (
        <Button family="outline" block={true} size="sm" onClick={clearFilter}>
          Clear
        </Button>
      )}
    </div>
  );
});
